import React, { useEffect, useState, useMemo } from 'react';
import { Music, Play, Pause, FileDown, Search } from 'lucide-react';
import { HERO_IMAGES } from '../mock';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import api, { fileUrl } from '../lib/api';

export default function Audio() {
  const [items, setItems] = useState([]);
  const [query, setQuery] = useState('');
  const [playingId, setPlayingId] = useState(null);

  useEffect(() => {
    api.listTeachings().then(list => {
      const audios = list.filter(t => t.audio_url).map(t => ({
        id: t.id,
        title: t.title,
        speaker: t.speaker || 'Pastor Oladipo Babalola',
        series: t.series || 'Teaching',
        duration: t.duration || '',
        description: t.description || '',
        audio_url: fileUrl(t.audio_url),
        thumbnail: t.thumbnail_url ? fileUrl(t.thumbnail_url) : HERO_IMAGES.bibleSunrise,
        pdf_url: t.pdf_url ? fileUrl(t.pdf_url) : null,
      }));
      setItems(audios);
    }).catch(() => setItems([]));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(i => !q || i.title.toLowerCase().includes(q) || i.speaker.toLowerCase().includes(q));
  }, [items, query]);

  const toggle = (id) => setPlayingId(prev => prev === id ? null : id);

  return (
    <div>
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="absolute inset-0">
          <img src={HERO_IMAGES.worshipHands2} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B27]/95 via-[#0B1B27]/80 to-[#0B1B27]/45" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-white">
          <p className="text-xs tracking-[0.35em] uppercase text-gold-brand mb-4">Listen &amp; Grow</p>
          <h1 className="font-serif-display text-5xl md:text-7xl leading-tight">Audio Sermons &amp; Podcasts</h1>
          <p className="mt-6 text-lg md:text-xl text-white/80 max-w-3xl">Take the Word with you \u2014 on the drive, on the walk, in the quiet of the night.</p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-ivory">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-8">
            <h2 className="font-serif-display text-3xl text-ink">Audio Library ({items.length})</h2>
            <div className="relative w-full md:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink/40" />
              <Input placeholder="Search audio\u2026" value={query} onChange={e => setQuery(e.target.value)} className="pl-9 bg-white" />
            </div>
          </div>

          {items.length === 0 ? (
            <Card className="p-12 bg-white rounded-2xl border border-ink/5 text-center">
              <Music className="h-10 w-10 mx-auto text-teal-deep mb-4" />
              <p className="font-serif-display text-2xl text-ink">Audio coming soon</p>
              <p className="text-ink/60 mt-2">MP3 sermons will appear here as the ministry uploads them.</p>
            </Card>
          ) : (
            <div className="space-y-4">
              {filtered.map(a => (
                <Card key={a.id} className="p-5 bg-white rounded-2xl border border-ink/5">
                  <div className="flex gap-5 items-start">
                    <div className="relative h-20 w-20 md:h-24 md:w-24 rounded-xl overflow-hidden flex-shrink-0">
                      <img src={a.thumbnail} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => toggle(a.id)} aria-label="Play" className="absolute inset-0 bg-black/30 hover:bg-black/50 flex items-center justify-center transition-colors">
                        {playingId === a.id ? <Pause className="h-7 w-7 text-white fill-white" /> : <Play className="h-7 w-7 text-white fill-white ml-0.5" />}
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <Badge className="bg-cream text-teal-deep border border-teal-brand/20">{a.series}</Badge>
                        {a.duration && <span className="text-xs text-ink/50">{a.duration}</span>}
                      </div>
                      <h3 className="font-serif-display text-xl md:text-2xl text-ink leading-snug">{a.title}</h3>
                      <p className="text-sm text-ink/60 mt-1">{a.speaker}</p>
                      {a.description && <p className="text-sm text-ink/60 mt-2 line-clamp-2">{a.description}</p>}
                      {playingId === a.id && (
                        <audio src={a.audio_url} controls autoPlay className="w-full mt-3" />
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <a href={a.audio_url} target="_blank" rel="noreferrer">
                          <Button variant="outline" className="h-9 px-3 border-teal-brand text-teal-deep hover:bg-teal-brand hover:text-white">
                            <FileDown className="h-4 w-4 mr-1.5" /> Download MP3
                          </Button>
                        </a>
                        {a.pdf_url && (
                          <a href={a.pdf_url} target="_blank" rel="noreferrer">
                            <Button variant="outline" className="h-9 px-3 border-gold-brand text-gold-deep hover:bg-gold-brand hover:text-ink">
                              <FileDown className="h-4 w-4 mr-1.5" /> Notes PDF
                            </Button>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
